import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Mic, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDuration } from "@/hooks/useAudioRecorder";

interface CallAudioPlayerProps {
  audioPath: string;
  durationSeconds: number | null;
}

export function CallAudioPlayer({ audioPath, durationSeconds }: CallAudioPlayerProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.storage
        .from('call-recordings')
        .createSignedUrl(audioPath, 3600);
      if (cancelled) return;
      if (error || !data?.signedUrl) {
        setError("Impossible de charger l'audio");
        setLoading(false);
        return;
      }
      setSignedUrl(data.signedUrl);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [audioPath]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="gap-1">
          <Mic className="w-3 h-3" />
          Enregistré
        </Badge>
        {durationSeconds != null && (
          <span className="text-xs text-muted-foreground font-mono">
            {formatDuration(durationSeconds)}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          Chargement de l'audio…
        </div>
      ) : error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : signedUrl ? (
        <audio controls src={signedUrl} className="w-full h-9" />
      ) : null}
    </div>
  );
}
